import { useEffect, useMemo, useState } from 'react'
import type { WalkStop } from '../lib/walkStops'
import { getMapStyle, getTileUpstream } from '../lib/mapConfig'
import { loadMapAsset } from '../lib/mapCacheProtocol'
import { evictMapCacheToCap, hasCachedAssets } from '../lib/mapTileCache'
import {
  expandBounds, OFFLINE_MARGIN_M, OFFLINE_ZOOMS, stopsBounds, styleAssetKeys, tileCountForBounds, tileKeysForBounds,
} from '../lib/offlineMapArea'

export type OfflineMapStatus = 'idle' | 'saving' | 'saved' | 'partial' | 'notSaved'

const CONCURRENCY = 4
/** [A3 final review] Ceiling on tile keys one save may fetch. A tight walk turf is a few hundred; one
 *  stray far-away geocode can blow the box up to ~160k z15 tiles, which would flood the hosted tile API
 *  on every turf open. Above it the pre-save is skipped (no fetches) and the status is 'partial'. */
export const OFFLINE_MAX_TILE_KEYS = 2000

/**
 * Automatic offline map save for the walk screen. Walking + online: fetch (through the cache protocol,
 * which stores) every tile/glyph/sprite the turf area needs. Offline: report whether it is all cached.
 * Driving or no located stops: 'idle' (driving caches only passively, via the protocol).
 */
export function useOfflineMapSave(p: {
  turfId: string
  stops: WalkStop[]
  mode: 'walking' | 'driving'
  isOnline: boolean
}): OfflineMapStatus {
  const { turfId, stops, mode, isOnline } = p

  const b = useMemo(() => stopsBounds(stops), [stops])
  const west = b?.west, south = b?.south, east = b?.east, north = b?.north
  // `tooBig`: the area is over OFFLINE_MAX_TILE_KEYS — counted first, so a huge box never builds its keys.
  const plan = useMemo<{ keys: string[] } | { tooBig: true } | null>(() => {
    if (west == null || south == null || east == null || north == null || mode !== 'walking') return null
    const area = expandBounds({ west, south, east, north }, OFFLINE_MARGIN_M)
    if (tileCountForBounds(area, OFFLINE_ZOOMS) > OFFLINE_MAX_TILE_KEYS) return { tooBig: true }
    return { keys: [...tileKeysForBounds(area, OFFLINE_ZOOMS), ...styleAssetKeys(getMapStyle())] }
  }, [west, south, east, north, mode])
  const keys = plan && 'keys' in plan ? plan.keys : null

  // Status is tagged with the scope it was computed for, so a turf/mode change never shows stale status.
  const scope = `${turfId}|${keys ? keys.length : 0}|${west},${south},${east},${north}|${mode}|${isOnline ? 'on' : 'off'}`
  const [state, setState] = useState<{ scope: string; status: OfflineMapStatus }>({ scope: '', status: 'idle' })

  useEffect(() => {
    if (!keys) return
    const ctrl = new AbortController()
    const finish = (status: OfflineMapStatus) => {
      if (!ctrl.signal.aborted) setState({ scope, status })
    }

    const run = async () => {
      if (!isOnline) {
        finish((await hasCachedAssets(keys)) ? 'saved' : 'notSaved')
        return
      }
      if (await hasCachedAssets(keys)) {
        finish('saved')
        return
      }
      finish('saving')
      let failed = false
      let next = 0
      const worker = async () => {
        while (!ctrl.signal.aborted && next < keys.length) {
          const key = keys[next++]
          try {
            await loadMapAsset(key, getTileUpstream(), { signal: ctrl.signal })
          } catch {
            failed = true
          }
        }
      }
      await Promise.all(Array.from({ length: CONCURRENCY }, worker))
      if (ctrl.signal.aborted) return
      // Tile failures resolve empty without being stored, so the cache is the source of truth.
      const complete = !failed && (await hasCachedAssets(keys))
      try {
        await evictMapCacheToCap()
      } catch {
        // Non-fatal: the cap is enforced again on the next save.
      }
      finish(complete ? 'saved' : 'partial')
    }
    void run()
    return () => ctrl.abort()
  }, [keys, isOnline, scope])

  if (plan && 'tooBig' in plan) return 'partial' // never pre-saved: only what the canvasser views is cached
  if (!keys) return 'idle'
  return state.scope === scope ? state.status : 'idle'
}
